import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
} from '@mui/material';

interface TermsSection {
  title: string;
  content: string[];
}

interface TermsOfServiceData {
  title: string;
  lastUpdatedDate: string;
  sections: TermsSection[];
}

const termsOfService: TermsOfServiceData = {
  title: 'Terms of Service',
  lastUpdatedDate: 'January 15, 2025',
  sections: [
    {
      title: '1. Acceptance of Terms',
      content: [
        'By accessing our website, requesting quotes, or using our manufacturing services, you agree to be bound by these Terms of Service.',
        'If you do not agree with any of these terms, you are prohibited from using our services or accessing our website.',
        'These terms apply to all clients, customers, and visitors of Monio Manufacturing.',
      ],
    },
    {
      title: '2. Manufacturing Services',
      content: [
        'Monio Manufacturing provides custom manufacturing services including textile production, furniture manufacturing, metalworks, and related services.',
        'All manufacturing services are subject to availability, technical feasibility, and our capacity to deliver.',
        'We reserve the right to decline projects that do not meet our quality standards or ethical guidelines.',
      ],
    },
    {
      title: '3. Quote and Payment Terms',
      content: [
        'All quotes are valid for 30 days from the date of issue unless otherwise specified.',
        'Payment terms are as follows: 50% deposit upon order confirmation, 50% upon completion before delivery.',
        'We accept payments via bank transfer, M-Pesa, and other approved payment methods.',
        'Late payments may result in additional charges and potential project delays.',
      ],
    },
    {
      title: '4. Project Specifications and Changes',
      content: [
        'All project specifications must be clearly defined and agreed upon before production begins.',
        'Changes to specifications after production has started may result in additional charges and delivery delays.',
        'We are not responsible for delays caused by client-requested changes or late provision of required materials.',
      ],
    },
    {
      title: '5. Quality Assurance and Delivery',
      content: [
        'We maintain strict quality control standards and conduct inspections at various stages of production.',
        'Delivery timelines are estimates and may vary based on project complexity and current workload.',
        'We will notify you of any significant delays and provide updated delivery schedules.',
      ],
    },
    {
      title: '6. Intellectual Property and Confidentiality',
      content: [
        'All designs, specifications, and proprietary information shared with us remain your property.',
        'We maintain strict confidentiality regarding your projects and business information.',
        'We may use completed projects for portfolio purposes unless you request otherwise in writing.',
      ],
    },
    {
      title: '7. Warranty and Returns',
      content: [
        'We provide warranty coverage for manufacturing defects for a period specified in your project agreement.',
        'Warranty does not cover damage from misuse, improper handling, or normal wear and tear.',
        'Returns must be reported within 7 days of delivery and must be in original condition.',
      ],
    },
    {
      title: '8. Limitation of Liability',
      content: [
        'Our liability is limited to the amount paid for the specific manufacturing service.',
        'We are not liable for indirect, incidental, or consequential damages.',
        'Force majeure events may affect our ability to deliver and are not covered by our liability.',
      ],
    },
    {
      title: '9. Termination and Cancellation',
      content: [
        'Either party may terminate a project with written notice, subject to cancellation fees.',
        'Cancellation fees depend on the stage of production and materials already procured.',
        'We reserve the right to terminate services for non-payment or breach of these terms.',
      ],
    },
    {
      title: '10. Governing Law and Disputes',
      content: [
        'These terms are governed by the laws of Kenya.',
        'Disputes will be resolved through negotiation, mediation, or arbitration as appropriate.',
        'Any legal proceedings will be conducted in the courts of Nairobi, Kenya.',
      ],
    },
  ],
};

export const TermsOfService: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      pt: 12,
      pb: 16,
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
          color: 'white',
          py: 8,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              mb: 3,
              textAlign: 'center',
            }}
          >
            {termsOfService.title}
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, opacity: 0.9, textAlign: 'center' }}>
            Last updated: {termsOfService.lastUpdatedDate}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.8, textAlign: 'center' }}>
            These terms govern your use of Monio Manufacturing services and outline our mutual rights and obligations.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Typography variant="body1" paragraph sx={{ mb: 6, fontSize: '1.1rem', lineHeight: 1.7 }}>
          Welcome to Monio Manufacturing! These Terms of Service govern your use of our manufacturing services, 
          website, and any interactions with our company. Please read these terms carefully before using our services. 
          By accessing our website or requesting our manufacturing services, you agree to be bound by these terms.
        </Typography>

        <Grid container spacing={4}>
          {termsOfService.sections.map((section, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Box sx={{ p: 4 }}>
                  <Typography variant="h4" gutterBottom sx={{
                    background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 600,
                    mb: 3,
                  }}>
                    {section.title}
                  </Typography>
                  {section.content.map((paragraph, pIndex) => (
                    <Typography key={pIndex} paragraph sx={{ 
                      lineHeight: 1.7,
                      color: 'text.secondary',
                      mb: 2,
                    }}>
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Contact Information */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Questions About These Terms?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            If you have any questions about these Terms of Service, please contact our legal team.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Email:</strong> legal@monio.com
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Phone:</strong> +254 700 000 000
          </Typography>
          <Typography variant="body1">
            <strong>Address:</strong> Monio Manufacturing, Nairobi, Kenya
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
